<script lang="ts">
  import type { ChatMessage, MessagePart, ToolCallPart, ThinkingPart } from "../../chat/types";
  import { Loader2, Sparkles, ShieldAlert } from "lucide-svelte";
  import { tick } from "svelte";
  import { getChatContext } from "../../chat/chat-runtime-context";
  import MarkdownContent from "../../chat/markdown-content.svelte";
  import ThinkingBlock from "./ThinkingBlock.svelte";
  import ToolCallBlock from "./ToolCallBlock.svelte";
  import AnalysisBlock from "./AnalysisBlock.svelte";
  import { i18n } from "../../chat/i18n.svelte";
  import logoUrl from "../../assets/vipex.png";

  const { t } = i18n;

  type MessageGroup =
    | { type: "user"; message: ChatMessage }
    | { type: "system"; message: ChatMessage }
    | { type: "assistant"; messages: ChatMessage[] };

  const chat = getChatContext();
  const runtimeState = chat.state;
  const adapter = chat.adapter;

  let container = $state<HTMLDivElement | null>(null);
  let shouldAutoScroll = true;
  /**
   * Count of tool parts currently waiting for the user (confirmation /
   * option selection). When this number INCREASES the LLM has just asked
   * the user something new — we force the view down regardless of where
   * the user has scrolled, otherwise the prompt can render off-screen
   * (notably when an AnalysisBlock is expanded and the user scrolled up).
   */
  let lastPendingCount = 0;

  function groupMessages(messages: ChatMessage[]): MessageGroup[] {
    const groups: MessageGroup[] = [];
    let currentAssistantGroup: ChatMessage[] = [];

    for (const message of messages) {
      if (message.role === "user") {
        if (currentAssistantGroup.length > 0) {
          groups.push({ type: "assistant", messages: currentAssistantGroup });
          currentAssistantGroup = [];
        }
        groups.push({ type: "user", message });
      } else if (message.role === "system") {
        if (currentAssistantGroup.length > 0) {
          groups.push({ type: "assistant", messages: currentAssistantGroup });
          currentAssistantGroup = [];
        }
        groups.push({ type: "system", message });
      } else {
        currentAssistantGroup.push(message);
      }
    }

    if (currentAssistantGroup.length > 0) {
      groups.push({ type: "assistant", messages: currentAssistantGroup });
    }

    return groups;
  }

  function flattenAssistantParts(messages: ChatMessage[]) {
    const allParts: { part: MessagePart; messageId: string; isLast: boolean }[] = [];

    for (let i = 0; i < messages.length; i++) {
      const message = messages[i];
      const isLastMessage = i === messages.length - 1;
      for (let j = 0; j < message.parts.length; j++) {
        allParts.push({
          part: message.parts[j],
          messageId: message.id,
          isLast: isLastMessage && j === message.parts.length - 1,
        });
      }
    }

    return allParts;
  }

  function handleScroll() {
    if (!container) return;
    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;
    shouldAutoScroll = distanceFromBottom < 100;
  }

  const groups = $derived(groupMessages($runtimeState.messages));
  const lastGroup = $derived(groups[groups.length - 1]);
  const isStreamingAssistant = $derived($runtimeState.isStreaming && lastGroup?.type === "assistant");

  $effect(() => {
    $runtimeState.messages;
    $runtimeState.isStreaming;

    // Count parts that need the user's attention; force-scroll on increase.
    let pendingCount = 0;
    for (const m of $runtimeState.messages) {
      for (const p of m.parts) {
        if (
          p.type === "toolCall" &&
          (p.status === "awaiting_confirmation" || p.status === "awaiting_selection")
        ) {
          pendingCount++;
        }
      }
    }
    const newPendingAppeared = pendingCount > lastPendingCount;
    lastPendingCount = pendingCount;

    void tick().then(() => {
      if (!container) return;
      if (newPendingAppeared) {
        // A new confirmation/selection just arrived — scroll regardless of
        // user position and re-arm auto-scroll so subsequent streaming
        // tokens also stay in view.
        container.scrollTop = container.scrollHeight;
        shouldAutoScroll = true;
      } else if (shouldAutoScroll) {
        container.scrollTop = container.scrollHeight;
      }
    });
  });
</script>

{#snippet renderTextPart(part: MessagePart, streaming: boolean)}
  {#if part.type === "text"}
    <div class="markdown-content" style="font-size: 13px; line-height: 1.65; font-family: var(--chat-font-sans);">
      <MarkdownContent text={part.text || ""} />
    </div>
  {/if}
{/snippet}

{#if $runtimeState.messages.length === 0}
<!-- Starbucks-inspired empty state -->
  <div style="
    flex: 1; display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    padding: 32px; text-align: center;
    background: var(--chat-bg);
  ">
    <div style="
      width: 56px; height: 56px;
      border-radius: 50%;
      background: transparent;
      display: flex; align-items: center; justify-content: center;
      margin-bottom: 24px;
      box-shadow: var(--chat-shadow-md);
      overflow: hidden;
    ">
      <img src={logoUrl} alt="Vipex Excel Agent" style="width: 100%; height: 100%; object-fit: cover;" />
    </div>
    <div style="font-size: 24px; font-weight: 600; color: var(--chat-brand-green); margin-bottom: 8px; font-family: var(--chat-font-sans); letter-spacing: -0.01em;">Vipex AI Agent</div>
    <div style="font-size: 16px; color: var(--chat-text-secondary); max-width: 240px; line-height: 1.5; font-family: var(--chat-font-sans);">
      {adapter.emptyStateMessage}
    </div>
  </div>
{:else}
  <div
    bind:this={container}
    onscroll={handleScroll}
    style="
      flex: 1; min-height: 0; overflow-y: auto;
      padding: 16px 12px;
      display: flex; flex-direction: column; gap: 4px;
      scrollbar-width: thin;
      scrollbar-color: var(--chat-scrollbar) transparent;
    "
  >
    {#each groups as group, index (group.type === "assistant" ? (group.messages[0]?.id || `asst-${index}`) : group.message?.id || `user-${index}`)}
      {#if group.type === "user"}
        <!-- Starbucks user bubble: right-aligned, Green Accent pill -->
        <div style="display: flex; justify-content: flex-end; padding: 6px 0;">
          <div style="
            max-width: 85%;
            padding: 10px 16px;
            background: var(--chat-user-bg);
            color: var(--chat-user-text, #fff);
            border-radius: 18px 18px 4px 18px;
            font-size: 12px;
            line-height: 1.5;
            font-family: var(--chat-font-sans);
            word-break: break-word;
            box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          ">
            {#each group.message.parts as part, partIndex (`${group.message.id}-${part.type}-${partIndex}`)}
              {#if part.type === "text"}
                {part.text}
              {/if}
            {/each}
          </div>
        </div>

      {:else if group.type === "system"}
        <!-- System / summary marker -->
        <div style="
          display: flex; align-items: center; gap: 6px;
          margin: 8px 16px;
          padding: 6px 12px;
          background: var(--chat-accent-subtle);
          border: 1px solid var(--chat-border);
          border-radius: var(--chat-radius-pill);
          font-size: 11px;
          color: var(--chat-text-muted);
          font-style: italic;
          font-family: var(--chat-font-sans);
          align-self: center;
        ">
          <Sparkles size={11} style="color: var(--chat-accent); flex-shrink: 0;" />
          History consolidated
        </div>

      {:else}
        <!-- Assistant message â€” flat, no bubble -->
        {@const allParts = flattenAssistantParts(group.messages)}
        {@const trailingTextStart = (() => {
          // Walk backwards to find where the final contiguous run of text parts begins
          let idx = allParts.length;
          for (let i = allParts.length - 1; i >= 0; i--) {
            if (allParts[i].part.type === "text") {
              idx = i;
            } else {
              break; // hit a non-text, stop
            }
          }
          return idx;
        })()}

        {@const analysisParts = allParts.slice(0, trailingTextStart)}
        {@const textParts = allParts.slice(trailingTextStart)}
        {@const isLastGroup = isStreamingAssistant && index === groups.length - 1}

        <div style="padding: 4px 0; display: flex; flex-direction: column; gap: 4px;">
          {#if analysisParts.length > 0}
            <AnalysisBlock parts={analysisParts} isStreaming={isLastGroup && textParts.length === 0} />
          {/if}

          {#if analysisParts.some(entry => entry.part.type === 'toolCall' && entry.part.status === 'awaiting_confirmation')}
            {@const pendingPart = (analysisParts.find(entry => entry.part.type === 'toolCall' && entry.part.status === 'awaiting_confirmation')!.part as ToolCallPart)}
            <div 
              class="fluent-fade-in"
              style="
                margin: 8px 0; padding: 16px;
                background: var(--chat-bg-secondary);
                border: 1px solid var(--chat-accent);
                border-radius: var(--chat-radius);
                box-shadow: var(--chat-shadow-md);
                display: flex; flex-direction: column; gap: 12px;
                z-index: 10;
              "
            >
              <div style="display: flex; align-items: flex-start; gap: 12px;">
                <div style="
                  flex-shrink: 0; width: 32px; height: 32px; border-radius: 50%;
                  background: var(--chat-accent-subtle);
                  display: flex; align-items: center; justify-content: center;
                  color: var(--chat-accent);
                ">
                  <ShieldAlert size={18} />
                </div>
                <div style="flex: 1; display: flex; flex-direction: column; gap: 4px;">
                  <div style="font-size: 14px; font-weight: 700; color: var(--chat-brand-green);">{t("confirmationRequired")}</div>
                  <div style="font-size: 12.5px; line-height: 1.5; color: var(--chat-text-secondary);">
                    {t("confirmationDescription")}
                  </div>
                </div>
              </div>
              
              <div style="display: flex; gap: 10px;">
                <button
                  type="button"
                  onclick={() => { 
                    console.log(`[MessageList] ðŸ›¡ï¸ CLICKED ALLOW for id=${pendingPart.id}`);
                    chat.confirmToolCall(pendingPart.id!, true);
                  }}
                  style="
                    flex: 1; padding: 10px; border-radius: var(--chat-radius-pill);
                    background: var(--chat-accent); color: white;
                    font-size: 13px; font-weight: 600;
                    border: 1px solid var(--chat-accent);
                    transition: transform 0.1s;
                    cursor: pointer;
                  "
                  onmousedown={(e) => (e.currentTarget.style.transform = 'scale(var(--button-active-scale))')}
                  onmouseup={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  {t("allowAction")}
                </button>
                <button
                  type="button"
                  onclick={() => { 
                    console.log(`[MessageList] ðŸ›¡ï¸ CLICKED REJECT for id=${pendingPart.id}`);
                    chat.confirmToolCall(pendingPart.id!, false);
                  }}
                  style="
                    flex: 1; padding: 10px; border-radius: var(--chat-radius-pill);
                    background: transparent; border: 1.5px solid var(--chat-text-primary);
                    color: var(--chat-text-primary);
                    font-size: 13px; font-weight: 600;
                    cursor: pointer;
                    transition: transform 0.1s;
                  "
                  onmousedown={(e) => (e.currentTarget.style.transform = 'scale(var(--button-active-scale))')}
                  onmouseup={(e) => (e.currentTarget.style.transform = 'scale(1)')}
                >
                  {t("reject")}
                </button>
              </div>
            </div>
          {/if}

          {#if analysisParts.some(entry => entry.part.type === 'toolCall' && (entry.part as ToolCallPart).name === 'ask_user' && entry.part.status === 'awaiting_selection')}
            {@const askPart = analysisParts.find(e => e.part.type === 'toolCall' && (e.part as ToolCallPart).name === 'ask_user' && e.part.status === 'awaiting_selection')!.part as ToolCallPart}
            {@const askQuestion = (askPart.args as any)?.question as string | undefined}
            {#if askQuestion}
              <div class="markdown-content fluent-fade-in" style="font-size: 13px; line-height: 1.65; font-family: var(--chat-font-sans);">
                <MarkdownContent text={askQuestion} />
              </div>
            {/if}
          {/if}

          <!-- User's chip selection rendered as a user bubble + processing indicator -->
          {#each analysisParts.filter(e => e.part.type === 'toolCall' && (e.part as ToolCallPart).name === 'ask_user' && e.part.status === 'complete' && (e.part as ToolCallPart).result) as entry (entry.part.id)}
            {@const askPart = entry.part as ToolCallPart}
            {@const selectedValue = (() => {
              try { return (JSON.parse(askPart.result!) as { selected?: string })?.selected ?? ""; }
              catch { return ""; }
            })()}
            {#if selectedValue}
              <div class="fluent-fade-in" style="display: flex; justify-content: flex-end; padding: 6px 0;">
                <div style="
                  max-width: 85%;
                  padding: 8px 14px;
                  background: var(--chat-user-bg);
                  color: var(--chat-user-text, #fff);
                  border-radius: 18px 18px 4px 18px;
                  font-size: 12px;
                  line-height: 1.5;
                  font-family: var(--chat-font-sans);
                  word-break: break-word;
                  box-shadow: 0 1px 2px rgba(0,0,0,0.1);
                ">
                  {selectedValue}
                </div>
              </div>
            {/if}
          {/each}

          {#if isLastGroup && textParts.length === 0 && analysisParts.some(e => e.part.type === 'toolCall' && (e.part as ToolCallPart).name === 'ask_user' && e.part.status === 'complete')}
            <div class="fluent-fade-in" style="display: flex; align-items: center; gap: 8px; padding: 6px 4px; font-size: 12px; color: var(--chat-text-muted); font-family: var(--chat-font-sans);">
              <Loader2 size={12} class="animate-spin" style="color: var(--chat-accent);" />
              <span>{i18n.locale === 'vi' ? 'Đang xử lý...' : 'Processing...'}</span>
            </div>
          {/if}

          {#each textParts as entry, partIndex (`${entry.messageId}-${entry.part.type}-${partIndex}`)}
            {@render renderTextPart(entry.part, isLastGroup && entry.isLast)}
          {/each}

          {#if isLastGroup && allParts.length === 0}
            <!-- Initial streaming indicator -->
            <div style="display: flex; align-items: center; gap: 8px; padding: 4px 0;">
              <AnalysisBlock parts={[]} isStreaming={true} />
            </div>
          {/if}
        </div>
      {/if}
    {/each}

    {#if $runtimeState.isUploading}
      <div style="
        display: flex; align-items: center; gap: 10px;
        margin: 8px 0; padding: 10px 14px;
        background: var(--chat-bg-tertiary);
        border: 1px solid var(--chat-border);
        border-radius: var(--chat-radius-lg);
        align-self: flex-start;
        max-width: 85%;
        box-shadow: 0 1px 2px rgba(0,0,0,0.05);
      ">
        <Loader2 size={16} class="animate-spin" style="color: var(--chat-accent);" />
        <div style="display: flex; flex-direction: column; gap: 2px;">
          <div style="font-size: 12px; font-weight: 600; color: var(--chat-text-primary); font-family: var(--chat-font-sans);">
            Äang trÃ­ch xuáº¥t dá»¯ liá»‡u...
          </div>
          <div style="font-size: 11px; color: var(--chat-text-muted); font-family: var(--chat-font-sans);">
            {$runtimeState.uploadingFileName}
          </div>
        </div>
      </div>
    {/if}
  </div>
{/if}
